import React, { useState } from "react";
import SourceChip from "../section/SourceChip.jsx";
import api from "../../api/client.js";
import AiPill from "./AiPill.jsx";
import { splitSentences } from "../../lib/sentences.js";
import AutoTextarea from "../AutoTextarea.jsx";

/**
 * The "Your role" field with a "Split into lines" affordance (Plan H): a
 * run-on paragraph becomes one sentence per line, which the caregiver's
 * page renders as bullets and the coordinator can tidy here.
 */
function RoleField({ label, value, onChange, placeholder }) {
  const text = value || "";
  const canSplit = !/\n/.test(text) && splitSentences(text).length >= 2;
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <AutoTextarea style={styles.textarea} value={text} onChange={onChange} rows={3} placeholder={placeholder} />
      <div style={styles.fieldFoot}>
        <span style={styles.hint}>One line per point reads as a list.</span>
        {canSplit && (
          <button type="button" style={styles.splitBtn}
                  onClick={() => onChange({ target: { value: splitSentences(text).join("\n") } })}>
            Split into lines
          </button>
        )}
      </div>
    </div>
  );
}

function GoalRow({ goal, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...goal });
  const [saving, setSaving] = useState(false);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/goals/${goal.id}`, {
        translated_text: form.translated_text,
        purpose: form.purpose || null,
        caregiver_role: form.caregiver_role || null,
      });
      onSave({ ...goal, ...form });
      setEditing(false);
    } catch (e) {
      alert(e.body?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm("Delete this goal?")) return;
    await api.delete(`/goals/${goal.id}`);
    onDelete(goal.id);
  }

  if (!editing) {
    return (
      <div style={styles.row}>
        <p style={styles.rowText}>{goal.translated_text}</p>
        <SourceChip source={goal.source} />
        <div style={styles.actions}>
          <AiPill type="draft" />
          <button style={styles.editBtn} onClick={() => setEditing(true)}>Edit</button>
          <button style={styles.deleteBtn} onClick={remove}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.editCard}>
      <div style={styles.field}>
        <label style={styles.label}>Support staff–friendly version</label>
        <AutoTextarea style={styles.textarea} value={form.translated_text} onChange={f("translated_text")} rows={3} />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Why it matters</label>
        <AutoTextarea style={styles.textarea} value={form.purpose || ""} onChange={f("purpose")} rows={2} placeholder="The motivation behind this goal" />
      </div>
      <RoleField label="Your role (caregiver)" value={form.caregiver_role} onChange={f("caregiver_role")}
                 placeholder="What the DSP should do" />
      <div style={styles.editActions}>
        <button style={styles.saveBtn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        <button style={styles.cancelBtn} onClick={() => { setForm({ ...goal }); setEditing(false); }}>Cancel</button>
      </div>
    </div>
  );
}

function AddGoalForm({ clientId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ original_text: "", translated_text: "", purpose: "", caregiver_role: "" });
  const [saving, setSaving] = useState(false);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  async function add() {
    if (!form.translated_text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/sections/working_on/goals`, {
        original_text: form.original_text || form.translated_text,
        translated_text: form.translated_text,
        purpose: form.purpose || null,
        caregiver_role: form.caregiver_role || null,
      });
      onAdded();
      setForm({ original_text: "", translated_text: "", purpose: "", caregiver_role: "" });
      setOpen(false);
    } catch (e) {
      alert(e.body?.error || "Add failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return <button style={styles.addBtn} onClick={() => setOpen(true)}>+ Add goal</button>;

  return (
    <div style={styles.editCard}>
      <div style={styles.field}>
        <label style={styles.label}>Support staff–friendly version <span style={{ color: "var(--color-critical)" }}>*</span></label>
        <AutoTextarea style={styles.textarea} value={form.translated_text} onChange={f("translated_text")} rows={3} placeholder="What this goal means in plain language" />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Original ISP text (optional)</label>
        <AutoTextarea style={styles.textarea} value={form.original_text} onChange={f("original_text")} rows={2} placeholder="Paste from ISP if available" />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Why it matters</label>
        <input style={styles.input} value={form.purpose} onChange={f("purpose")} />
      </div>
      <RoleField label="Support staff's role" value={form.caregiver_role} onChange={f("caregiver_role")} />
      <div style={styles.editActions}>
        <button style={styles.saveBtn} onClick={add} disabled={saving || !form.translated_text.trim()}>{saving ? "Adding…" : "Add goal"}</button>
        <button style={styles.cancelBtn} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function GoalEditor({ clientId, initialGoals = [], onRefresh }) {
  const [goals, setGoals] = useState(initialGoals);

  return (
    <div style={styles.root}>
      <h3 style={styles.heading}>Goals</h3>
      {goals.map(g => (
        <GoalRow key={g.id} goal={g}
          onSave={updated => setGoals(prev => prev.map(x => x.id === updated.id ? updated : x))}
          onDelete={id => setGoals(prev => prev.filter(x => x.id !== id))}
        />
      ))}
      <AddGoalForm clientId={clientId} onAdded={onRefresh} />
    </div>
  );
}

const styles = {
  root: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  heading: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-tertiary)", paddingBottom: "var(--space-2)", borderBottom: "1px solid var(--color-border)" },
  row: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-4)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-4)" },
  rowText: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", flex: 1 },
  actions: { display: "flex", gap: "var(--space-2)", flexShrink: 0 },
  editBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-3)", cursor: "pointer" },
  deleteBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer" },
  editCard: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  field: { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  fieldFoot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-2)" },
  hint: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  splitBtn: { font: "inherit", fontSize: "var(--text-xs)", padding: "2px 10px", borderRadius: 999, border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text-secondary)", cursor: "pointer" },
  label: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-tertiary)" },
  input: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%" },
  textarea: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%", resize: "vertical", fontFamily: "inherit" },
  editActions: { display: "flex", gap: "var(--space-3)" },
  saveBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  cancelBtn: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  addBtn: { fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", alignSelf: "flex-start" },
};
